import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Text from '../Text';
import AddAttachment from '../TileForm/components/AddAttachment';
import fileThumb from '../../assets/missing-file-img-thumb.png';

const Thumbnail = ({ attachments, attachment }) => {
  const extension = attachment.split('.')[attachment.split('.').length - 1];
  switch (extension.toLowerCase()) {
    case 'pdf':
    case 'txt': {
      return (
        <Iframe
          width="84"
          height="84"
          src={attachments[attachment]}
          type="application/pdf"
        />
      );
    }
    case 'mp4':
    case 'mpeg':
    case 'wmv': {
      return <Video src={attachments[attachment]} />;
    }
    case 'bmp':
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'svg': {
      return (
        <Image
          alt={`${attachment.replace(/ /g, '_')}`}
          src={attachments[attachment]}
        />
      );
    }
    case 'zip': {
      return (
        <Image>
          <Icon
            className="fa-regular fa-file-zipper fa-5x"
            aria-hidden="true"
          />
        </Image>
      );
    }
    case 'xls':
    case 'csv':
    case 'xlsx': {
      return (
        <Image>
          <Icon className="fa-regular fa-table fa-5x" aria-hidden="true" />
        </Image>
      );
    }
    case 'doc':
    case 'docx': {
      return (
        <Image>
          <Icon className="fa-regular fa-file-word fa-5x" aria-hidden="true" />
        </Image>
      );
    }
    default: {
      return <Image alt={`${attachment.replace(/ /g, '_')}`} src={fileThumb} />;
    }
  }
};

Thumbnail.propTypes = {
  attachment: PropTypes.string,
  attachments: PropTypes.object,
};

const Attachments = ({ attachments, removeAttachment, addAttachment }) => {
  return (
    <Wrapper>
      {Object.keys(attachments).map((attachment, key) => (
        <Container
          key={`${attachment.replace(/ /g, '_')}${key}`}
          title={attachment}
        >
          {removeAttachment && (
            <AttachmentDelete
              className="fa-regular fa-trash"
              name={attachment}
              onClick={removeAttachment}
            />
          )}
          <Box
            href={attachments[attachment]}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Thumbnail attachments={attachments} attachment={attachment} />
            <Title>
              <Text variant="subtitle2" as="span" color="black.80">
                {attachment}
              </Text>
            </Title>
          </Box>
        </Container>
      ))}
      {addAttachment && (
        <AddAttachment
          addAttachmentHandler={addAttachment}
          attachments={attachments}
        />
      )}
    </Wrapper>
  );
};

Attachments.propTypes = {
  attachments: PropTypes.object,
  removeAttachment: PropTypes.func,
  addAttachment: PropTypes.func,
};

export default Attachments;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0px;
`;

const Container = styled.div`
  position: relative;
  width: 48%;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Box = styled.a`
  display: flex;
  border-radius: 8px;
  border: solid 1px ${themeGet('colors.black.10')};
  margin: 10px 0px;
  @media (max-width: 400px) {
    max-width: 200px;
  }
  &:hover {
    box-shadow: 0px 8px 20px 0px ${themeGet('colors.black.10')};
  }
`;

const Image = styled.div`
  height: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
  background-image: url('${props => props.src}');
  background-size: cover;
  background-position: center;
`;

const Icon = styled.i`
  color: ${themeGet('colors.primary.100')};
`;

const Iframe = styled.iframe`
  height: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
`;

const Video = styled.video`
  height: 84px;
  width: 84px;
  min-width: 84px;
  margin: 8px;
  margin-right: 14px;
  border-radius: 8px;
`;

const Title = styled.div`
  margin-top: 42px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
  max-width: 300px;
`;

const AttachmentDelete = styled.i`
  cursor: pointer;
  color: ${themeGet('colors.black.80')};
  position: absolute;
  width: 14px;
  top: 25px;
  right: 8px;
`;
